import {
  qPatientFamilyHistory,
  qPatientFamilyMembers,
  qPatientFamilyMemberAllergies,
  qPatientFamilyMemberMedications,
  groupFamilyHistory,
  type FamilyHistoryVM,
  type FamilyMemberVM,
  type PatientFamilyHistoryRow,
  type PatientFamilyMemberRow,
  type PatientFamilyMemberAllergyRow,
  type PatientFamilyMemberMedicationRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../adapters/powersync';

/**
 * PRD.267 — read-only Family Allergies & Medications display, grouped by family
 * member. Rendered in the patient workspace/sidebar, visually separated from
 * the patient's OWN allergies and medications. Family records here never appear
 * in the patient's own allergy alert chips or medication list.
 */

function titleCase(v: string): string {
  return v
    .toLowerCase()
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function memberTitle(m: FamilyMemberVM): string {
  const rel =
    m.relationshipType === 'OTHER' && m.relationshipLabel
      ? m.relationshipLabel
      : titleCase(m.relationshipType || 'Family member');
  return m.name ? `${rel} — ${m.name}` : rel;
}

const STATUS_EMPTY_TEXT: Record<string, string> = {
  NONE_KNOWN: 'No known family allergies or medications.',
  UNKNOWN: 'Family allergy and medication history unknown.',
  NOT_ASKED: 'Family allergy and medication history not asked.',
};

export function FamilyHistoryView({ history }: { history: FamilyHistoryVM }) {
  const membersWithData = history.members.filter(
    (m) => m.allergies.length > 0 || m.medications.length > 0,
  );
  const showEmpty = history.historyStatus !== 'HAS_HISTORY' || membersWithData.length === 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        borderTop: '1px solid var(--border)',
        paddingTop: 'var(--sp-4)',
        marginTop: 'var(--sp-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <span style={{ fontWeight: 'var(--font-weight-semi)', color: 'var(--text)' }}>
          Family History
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: '1px var(--sp-2)',
          }}
        >
          Not the patient’s own allergies/medications
        </span>
      </div>

      {showEmpty ? (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {STATUS_EMPTY_TEXT[history.historyStatus] ?? STATUS_EMPTY_TEXT.NOT_ASKED}
        </span>
      ) : (
        membersWithData.map((m) => (
          <div
            key={m.id}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--sp-3)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-1)',
            }}
          >
            <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
              {memberTitle(m)}
            </span>
            {m.allergies.map((a) => (
              <span key={a.id} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                Allergy: {[a.substance, a.reaction, a.severity && titleCase(a.severity)]
                  .filter(Boolean)
                  .join(' — ')}
              </span>
            ))}
            {m.medications.map((med) => (
              <span key={med.id} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                Medication: {[med.name, med.dose, med.status && titleCase(med.status)]
                  .filter(Boolean)
                  .join(' — ')}
              </span>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

/** Data-loading wrapper: reads the four family tables and renders the view. */
export function FamilyHistorySection({ patientId }: { patientId: string }) {
  const { data: historyRows = [] } = usePowerSyncQuery<PatientFamilyHistoryRow>(
    qPatientFamilyHistory(patientId),
  );
  const { data: memberRows = [] } = usePowerSyncQuery<PatientFamilyMemberRow>(
    qPatientFamilyMembers(patientId),
  );
  const { data: allergyRows = [] } = usePowerSyncQuery<PatientFamilyMemberAllergyRow>(
    qPatientFamilyMemberAllergies(patientId),
  );
  const { data: medicationRows = [] } = usePowerSyncQuery<PatientFamilyMemberMedicationRow>(
    qPatientFamilyMemberMedications(patientId),
  );

  const history = groupFamilyHistory(historyRows[0], memberRows, allergyRows, medicationRows);
  return <FamilyHistoryView history={history} />;
}
